import { observer } from 'mobx-react'
import { lazy, useEffect } from 'react'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import routes from 'routes'
import VerticalLayout from 'components/Layouts/VerticalLayout'
import { AuthenticateParams } from 'configs/constants/enums'
import { AuthRoleIdEnum } from 'constants/user'
import { useStores } from 'hooks/useStores'
import isEmpty from 'lodash/isEmpty'
import PlanStationDetail from 'components/Pages/PlansPage/components/PlanStationDetail'
import DashboardPage from 'pages/DashboardPage'
import LoginPage from 'pages/LoginPage'
import ResetPasswordPage from 'pages/ResetPasswordPage'
import SearchPage from 'pages/SearchPage'

const AppearancePage = lazy(() => import('pages/AppearancePage'))
const ArchivePage = lazy(() => import('pages/ArchivePage'))
const CollectionDetailPage = lazy(() => import('pages/CollectionDetailPage'))
const CollectionsPage = lazy(() => import('pages/CollectionsPage'))
const CommonLibraryLayout = lazy(() => import('pages/CommonLibraryPage'))
const DocumentTypePage = lazy(() => import('pages/DocumentTypePage'))
const ForgotPasswordPage = lazy(() => import('pages/ForgotPasswordPage'))
const GroupPage = lazy(() => import('pages/GroupPage'))
const GroupDetailPage = lazy(() => import('pages/GroupUserPage'))
const IconBuilderPage = lazy(() => import('pages/IconBuilderPage'))
const InboxPage = lazy(() => import('pages/InboxPage'))
const NotificationPage = lazy(() => import('pages/NotificationPage'))
const OrganizationDetailPage = lazy(() => import('pages/OrganizationDetailPage'))
const OrganizationReportPage = lazy(() => import('pages/OrganizationReportPage'))
const ProcessDetailPage = lazy(() => import('pages/ProcessDetailPage'))
const ProcessPage = lazy(() => import('pages/ProcessPage'))
const PlansPage = lazy(() => import('components/Pages/PlansPage'))
const WorkPlanDetailPage = lazy(() => import('pages/WorkPlanDetailPage'))
const WorkPlanPreviewPage = lazy(() => import('pages/WorkPlanPreviewPage'))
const TagPage = lazy(() => import('pages/TagPage'))
const UserDetailPage = lazy(() => import('pages/UserDetailPage'))
const UserPage = lazy(() => import('pages/UserPage'))
const SSOConfigPage = lazy(() => import('pages/SSOConfig'))
const AccountSettingsPage = lazy(() => import('pages/AccountSettingsPage'))

const SubDomainRoutes = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const pathName = location.pathname
  const { authStore, userStore, organizationStore } = useStores()
  const { organization } = organizationStore
  const { currentUser } = userStore
  const { userDetail } = authStore
  const authRoleId = Number(userDetail?.authRoleId ?? 0)
  const isBasicUser = authRoleId === AuthRoleIdEnum.BASIC_USER
  const accessToken: string = localStorage.getItem(AuthenticateParams.ACCESS_TOKEN) ?? ''
  const isLoggedIn: boolean = !!accessToken

  // Org setting: Work Plans visibility (undefined/null = enabled for backward compat)
  const isWorkPlanEnabled = organization?.isWorkPlanEnabled !== false

  // Handle notification clicks from service worker
  useEffect(() => {
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type === 'NOTIFICATION_CLICK') {
        const { targetUrl, notificationData } = event.data
        console.log('Handling notification click navigation to:', targetUrl)
        console.log('Notification data received:', notificationData)
        navigate(targetUrl)
      }
    }

    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage)

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage)
    }
  }, [navigate])

  useEffect(() => {
    if (!organization || !pathName) return
    // Guard Work Plans routes by org setting (for all roles)
    if (!isWorkPlanEnabled && (pathName === routes.plans.value || pathName.startsWith('/plans') || pathName.startsWith('/station'))) {
      toast.error('You are not allowed to access this page, redirecting back.')
      navigate(routes.dashboard.value, { replace: true })
      return
    }

    const isThemeSetting = organization?.isThemeSetting ?? true
    const isReportTool = organization?.isReportTool ?? true
    const isCollectionFeature = organization?.isCollectionFeature ?? true
    const isStandardUserCanAccessUser = organization?.isStandardUserCanAccessUser ?? false

    if (
      (!isThemeSetting && pathName.includes(routes.setting.appearance.value)) ||
      (!isReportTool && pathName.includes(routes.report.organization.value)) ||
      (!isCollectionFeature && pathName === routes.collections.value) ||
      (!isStandardUserCanAccessUser && isBasicUser && pathName.includes(routes.users.value))
    ) {
      navigate(routes.dashboard.value)
      toast.error('You are not allowed to access this page, redirecting back.')
      return
    }

    // Block non-ORG_ADMIN from accessing SSO Configuration page
    if (pathName.includes(routes.setting.sso.value) && Number(authRoleId) > 0) {
      const isOrgAdmin = Number(authRoleId) === AuthRoleIdEnum.ORG_ADMIN
      
      if (!isOrgAdmin) {
        navigate(routes.dashboard.value)
        toast.error('You are not allowed to access this page, redirecting back.')
        return
      }
    }

    if (!isBasicUser) return

    if (
      pathName.includes(routes.setting.value) ||
      (!currentUser?.isReportTool && pathName.includes(routes.report.organization.value)) ||
      pathName.includes(routes.groups.value)
    ) {
      navigate(routes.dashboard.value)
      toast.error('You are not allowed to access this page, redirecting back.')
      return
    }

    if (
      !pathName.includes(routes.commonLibrary.value) &&
      !pathName.includes(routes.processes.value) &&
      !pathName.includes(routes.archive.value)
    ) {
      return
    }

    if (!(!isEmpty(currentUser) ? currentUser.isEditor : true)) {
      navigate(routes.dashboard.value)
      toast.error('You are not allowed to access this page, redirecting back.')
    }
  }, [currentUser, pathName, organization, authRoleId, isWorkPlanEnabled])


  if (isLoggedIn) {
    // all logged-in routes should be under VerticalLayout
    return (
      <Routes>
        <Route element={<VerticalLayout />}>
            <Route path={routes.dashboard.value} element={<DashboardPage />} />
            <Route path={routes.commonLibrary.value} element={<CommonLibraryLayout />} />
            <Route path={routes.collections.value} element={<CollectionsPage />} />
            <Route path="/collections/:collectionId" element={<CollectionDetailPage />} />
            <Route path={routes.processes.value} element={<ProcessPage />} />
            <Route path={routes.search.value} element={<SearchPage />} />
            <Route path={routes.archive.value} element={<ArchivePage />} />
            {isWorkPlanEnabled && <Route path={routes.plans.value} element={<PlansPage />} />}
            {isWorkPlanEnabled && <Route path="/plans/:id" element={<WorkPlanDetailPage />} />}
            {isWorkPlanEnabled && <Route path="/station/:id" element={<PlanStationDetail />} />}
            <Route path="/processes/:processId" element={<ProcessDetailPage />} />
            <Route path={routes.notifications.value} element={<NotificationPage />} />
            <Route path={routes.messages.value} element={<InboxPage />} />
            <Route path={routes.users.value} element={<UserPage />} />
            <Route path="/users/:userId" element={<UserDetailPage />} />
            <Route path={routes.groups.value} element={<GroupPage />} />
            <Route path={routes.setting.documentType.value} element={<DocumentTypePage />} />
            <Route path={routes.setting.iconBuilder.value} element={<IconBuilderPage />} />
            <Route path={routes.setting.tag.value} element={<TagPage />} />
            <Route path={routes.setting.appearance.value} element={<AppearancePage />} />
            <Route path={routes.setting.sso.value} element={<SSOConfigPage />} />
            <Route path={routes.account.value} element={<AccountSettingsPage />} />
            <Route path="/groups/:groupId" element={<GroupDetailPage />} />
            <Route path={routes.report.organization.value} element={<OrganizationReportPage />} />
            <Route path={`${routes.report.organization.value}/:id`} element={<OrganizationDetailPage />} />
            <Route path="*" element={<DashboardPage />} />
          </Route>
        {isWorkPlanEnabled && <Route path={routes.plans.previewId.value(':id')} element={<WorkPlanPreviewPage />} />}
        </Routes>
    )
  }
  // fallback to login page if no accesstoken found
  return (
    <Routes>
      <Route path={routes.home.value} element={<LoginPage />} />
      <Route path={routes.login.value} element={<LoginPage />} />
      <Route path={routes.forgotPassword.value} element={<ForgotPasswordPage />} />
      <Route path={routes.resetPassword.value} element={<ResetPasswordPage />} />
      <Route path="*" element={<LoginPage />} />
    </Routes>
  )
}

export default observer(SubDomainRoutes)
